import { useEffect, useState } from 'react';
import { CheckCheck, Loader, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { z } from 'zod';
import moment from "moment";
import { SummaryFiltersValidation, useListRiskSummaryFull } from '@/lib/api/risk/summary';
import { CommonDestinationTimezone } from '@/lib/utils';
import { useSearchParams } from 'react-router-dom';
import '@/styles/riskCompactTable.css';
import { useQueryParam } from '@/hooks/use-query-param';
import { useQueryParamArray } from '@/hooks/use-query-param-array';
import { Skeleton } from '@/components/ui/skeleton';
import { BasicFilters } from './TopLevelFilters';
import { CustomerView } from './CustomerView';
import { TopLevelSummaryTable } from './TopLevelSummaryTable';
import { CustomersTopLevelViewSummary } from './CustomersTopLevelViewSummary';
import { CustomerCommodityView } from './CustomerCommodityView';
import { CustomerFullDetailView } from './CustomerFullDetailView';
import { Button } from '@/components/ui/button';
import { cn } from "@/lib/utils";
import { ManageCashSales } from './ManageCashSales';
import { FullUnknownSection } from "./FullUnknownSection";
import { RowFlagStatus } from '@/lib/api/risk/cashSales/general';
import { CustomerActivitiesSection } from '@/components/modals/CustomerActivitiesSection';
import { RiskRevenueSection } from './RiskRevenue/RevenueSection';
import { generateCurrentDefaultCommodityYear } from "./dates";
import { getRiskCoreCommoditiesDisplayNames } from '@/lib/api/commodity';

type ActiveTab = "summary";

export function RiskManagementFullDashboard() {
    const [activeTab, setActiveTab] = useState<ActiveTab>("summary");

    if (activeTab === "summary") {
        return <SummaryTab />
    }

    return null;
}

function SummaryTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentTab, setSelectedCurrentTab] = useQueryParam("overview_tab", 'view');
    const [selectedCropYear, _selectedCropYear] = useQueryParam("crop_year", generateCurrentDefaultCommodityYear());
    const [commodityDisplayName, _selectedCommodityDisplayName] = useQueryParam("commodity", "");
    const [customerIds, _customerIds] = useQueryParamArray("customer_ids");
    const [traderIds, _traderIds] = useQueryParamArray("trader_ids");
    const [detailView, _detailView] = useQueryParam("detail_view", "");
    const [filtersCollapsed, setFiltersCollapsed] = useState(true);

    const hasCustomerSelected = customerIds?.length === 1;
    const hasNoCustomersSelected = customerIds?.length === 0;
    const hasMultipleCustomersSelected = customerIds?.length > 1;
    const shouldShowTopLevelSummary = hasNoCustomersSelected || hasMultipleCustomersSelected;
    const shouldShowTopLevelViewsummary = (hasNoCustomersSelected || hasMultipleCustomersSelected) && currentTab === 'view';
    const isDetailView = !!detailView;

  const excludedCustomerIds = shouldShowTopLevelSummary ? [
    'be2a7e63-0be3-4e50-af6c-d9a25cdad79c', // lighthouse customer risk
    'c537951a-1afb-4172-b3a8-b566a20999a8', // Andrew
    '6f2cc3b6-8753-42b4-bd36-5ddfb5153128', // Pat
  ] : [];

    const filters: z.infer<typeof SummaryFiltersValidation> = {
        is_enabled: true, crop_year: parseInt(selectedCropYear), trader_ids: traderIds, customer_ids: customerIds,
      display_commodity_type: shouldShowTopLevelViewsummary ? getRiskCoreCommoditiesDisplayNames() : commodityDisplayName ? [commodityDisplayName] : undefined,
        flag_status: ["", RowFlagStatus.SurfaceAndIncludeInStats],
        include_full_revenue_details: currentTab === 'revenue',
      exclude_customer_ids: excludedCustomerIds?.length > 0 ? excludedCustomerIds : undefined,
    };
    const baseTopLevelResult = useListRiskSummaryFull({ filters });

    const summaryData = baseTopLevelResult?.data?.data?.summary || {};
    const customerId = customerIds?.length === 1 ? customerIds[0] : "";
    const customerName = customerId ? summaryData?.customer_name_map?.[customerId] || "" : "";



    const isLoading = baseTopLevelResult?.isLoading;
    const isRefreshing = baseTopLevelResult?.isFetching;
    const encounteredError = !isLoading && (!!baseTopLevelResult?.error || !baseTopLevelResult?.data?.success);

    useEffect(() => {
        if (!!detailView && !commodityDisplayName && customerIds.length === 1) {
            setSearchParams((prev) => {
                prev.delete("detail_view");
                return prev;
            });
        }

    }, [detailView, customerIds, commodityDisplayName]);

    const toView = [];
    switch (currentTab) {
        case "view":
            if (shouldShowTopLevelSummary) {
              toView.push(<TopLevelSummaryTable baseTopLevelResult={baseTopLevelResult} key="risk-view-top-level-summary" />)
            }
            break;
        case "manage":
            toView.push(<ManageCashSales key="risk-detail-manage-cash-sales" />)
            break;
        case "activities":
            toView.push(<CustomerActivitiesSection customerId={customerIds?.length === 1 ? customerIds[0] : undefined} traderId={traderIds?.length > 0 ? traderIds[0] : undefined} showCustomerNames key="risk-detail-customer-activities" />);
            break;
        case "unknown":
            toView.push(<FullUnknownSection key="risk-view-top-full-unknown" />);
            break;
        case "revenue":
            toView.push(<RiskRevenueSection key="risk-view-revenue-page" baseTopLevelResult={baseTopLevelResult} />);
            break;
    }

    if (shouldShowTopLevelViewsummary) {
        toView.push(<CustomersTopLevelViewSummary baseTopLevelResult={baseTopLevelResult} key="risk-view-top-level-summary-customers" />);
    }

    if (hasCustomerSelected && currentTab === 'view') {
        if (!commodityDisplayName && !isDetailView) {
            toView.push(<CustomerView baseTopLevelResult={baseTopLevelResult} customerId={searchParams.get("customer_ids")!} key="risk-detail-customer-view" />);
        } else if (!!commodityDisplayName && !isDetailView) {
            toView.push(<CustomerCommodityView baseTopLevelResult={baseTopLevelResult} customerId={searchParams.get("customer_ids")!} commodityDisplayName={commodityDisplayName} key="risk-detail-customer-commodity-view" />);
        } else if (!!commodityDisplayName && isDetailView) {
            toView.push(<CustomerFullDetailView baseTopLevelResult={baseTopLevelResult} customerId={searchParams.get("customer_ids")!} commodityDisplayName={commodityDisplayName} top_level_filters={filters} key="risk-detail-full-detail-view" />);
        }
    }

    // Helper function to truncate title
    const getTruncatedTitle = () => {
        if (customerId && !!summaryData?.customer_name_map) {
            const fullTitle = `${customerName}`;
            // On mobile, truncate to 20 chars, on larger screens show more
            if (fullTitle.length > 30) {
                return (
                    <span className="block truncate max-w-[200px] sm:max-w-[300px] lg:max-w-none" title={fullTitle}>
                        {fullTitle}
                    </span>
                );
            }
            return fullTitle;
        }
        return "Risk Summary";
    };

    return (
        <div className="flex flex-col gap-1 overflow-hidden max-w-full">
            <div className="flex flex-col gap-1">
                {/* Header section with improved responsive layout */}
                <div className={cn(filtersCollapsed ? "flex flex-row gap-2" : "flex flex-col lg:flex-row gap-2")}>
                    {/* Title and tabs section */}
                    <div className={cn("w-full flex gap-4", !filtersCollapsed ? "flex-col 3xl:flex-row 3xl:justify-between 3xl:items-center" : "flex-col md:flex-row md:justify-between")}>
                        <div className="flex flex-col md:flex-row md:items-start">
                            <div className="flex flex-col items-start">
                                {isLoading && (<h1 className="text-2xl font-bold"><Skeleton className="w-[200px] h-[24px]" /></h1>)}
                                {!isLoading && (<h1 className="text-xl sm:text-2xl font-bold">{getTruncatedTitle()}</h1>)}
                                <div className="flex flex-row mt-1">
                                    {!isLoading && encounteredError && (<p className="text-red-500 flex flex-row text-sm">Error loading data</p>)}
                                    {!isLoading && isRefreshing && (<p className="text-muted-foreground flex flex-row text-sm">Refreshing data... <Loader className="w-4 h-4 animate-spin" /></p>)}
                                    {(!isLoading && !isRefreshing && !encounteredError) ? (
                                        <p className="text-muted-foreground flex flex-row text-sm hidden sm:block">Data Loaded</p>
                                    ) : null}
                                </div>
                            </div>

                            {/* Tabs section */}
                            {(!(isLoading || encounteredError)) && (
                                <div className="text-sm font-medium text-center text-gray-500 md:ml-4">
                                    <ul className="flex flex-wrap -mb-px">
                                        <Button variant="outline" className={cn(
                                            currentTab === 'view' ? "inline-block p-2 sm:p-4 text-red-600 border-0 border-b-2 border-red-600 rounded-t-lg active" : "inline-block p-2 sm:p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300",
                                            "text-xs sm:text-sm"
                                        )} onClick={() => setSelectedCurrentTab("view")} >
                                            View
                                        </Button>
                                        <Button variant="outline" className={cn(
                                            currentTab === 'manage' ? "inline-block p-2 sm:p-4 text-red-600 border-0 border-b-2 border-red-600 rounded-t-lg active" : "inline-block p-2 sm:p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300",
                                            "text-xs sm:text-sm"
                                        )} onClick={() => setSelectedCurrentTab("manage")}>
                                            <span className="hidden sm:inline">Manage</span>
                                            <span className="sm:hidden">Manage</span>
                                        </Button>
                                        <Button variant="outline" className={cn(
                                            currentTab === 'activities' ? "inline-block p-2 sm:p-4 text-red-600 border-0 border-b-2 border-red-600 rounded-t-lg active" : "inline-block p-2 sm:p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300",
                                            "text-xs sm:text-sm"
                                        )} onClick={() => setSelectedCurrentTab("activities")}>
                                            Activities
                                        </Button>
                                        <Button variant="outline" className={cn(
                                            currentTab === 'unknown' ? "inline-block p-2 sm:p-4 text-red-600 border-0 border-b-2 border-red-600 rounded-t-lg active" : "inline-block p-2 sm:p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300",
                                            "text-xs sm:text-sm"
                                        )} onClick={() => setSelectedCurrentTab("unknown")}>
                                            Unknown
                                        </Button>
                                        <Button variant="outline" className={cn(
                                            currentTab === 'revenue' ? "inline-block p-2 sm:p-4 text-red-600 border-0 border-b-2 border-red-600 rounded-t-lg active" : "inline-block p-2 sm:p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300",
                                            "text-xs sm:text-sm"
                                        )} onClick={() => setSelectedCurrentTab("revenue")}>
                                            Revenue
                                        </Button>
                                    </ul>
                                </div>
                            )}
                        </div>

                        <BasicFilters filtersCollapsed={filtersCollapsed} setFiltersCollapsed={setFiltersCollapsed} />
                    </div>
                </div>
            </div>

            {toView}
        </div>
    );
}

export default RiskManagementFullDashboard;

